import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  FP_REASON_LABEL, KeyStatusBadge, PageHeader, RiskBadge, SecretsTabs, errMsg, fmtDateTime, invalidateFilters,
  useSecretFilters, useSecretsAccess,
} from './common';

// False-positive rules: proposals from the learner and seeds, hand-written
// rules, approval, backlog apply, and suspension.

const TYPE_LABEL = { path_glob: 'Path', key_name: 'Assigned to', shape: 'Value shape' };
const STATUS_STYLE = {
  proposed: { bg: 'rgba(99,102,241,0.2)', fg: '#a5b4fc' },
  approved: { bg: 'rgba(34,197,94,0.2)', fg: '#4ade80' },
  suspended: { bg: 'rgba(245,158,11,0.2)', fg: '#fbbf24' },
  rejected: { bg: 'rgba(100,116,139,0.15)', fg: '#94a3b8' },
};

function RuleStatus({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.rejected;
  return <span className="badge" style={{ background: s.bg, color: s.fg, whiteSpace: 'nowrap' }}>{status}</span>;
}

function Precision({ r, floors }) {
  if (r.precision_pct === null || r.precision_pct === undefined) {
    return <span style={{ color: 'var(--text-muted)' }}>no labels yet</span>;
  }
  const ok = r.meets_floor;
  return (
    <span style={{ color: ok ? '#4ade80' : '#fbbf24' }} title={`${r.support} labelled false positives, ${r.matches_real} labelled real`}>
      {Number(r.precision_pct).toFixed(0)}% on {r.support + r.matches_real}
      {!ok && <span style={{ color: 'var(--text-muted)' }}> (floor {floors.precision_min}% on {floors.support_min})</span>}
    </span>
  );
}

function RuleDetail({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`/secrets/fp-rules/${id}`).then((r) => setD(r.data)).catch(() => {}); }, [id]);
  if (!d) return <div style={{ padding: 12 }}><span className="spinner" /></div>;
  const c = d.counts;
  return (
    <div style={{ padding: '8px 16px 12px 44px', background: 'var(--bg-base)' }}>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
        Matches {c.fp} labelled false positives, {c.real} labelled real keys, {c.open} open keys it would mark, {c.other} others.
        {' '}<Link to={`/secrets/keys?fp_rule_id=${id}`}>Keys it marked</Link>
      </div>
      <table>
        <thead><tr><th>Key</th><th>Risk</th><th>Status</th><th>Shape · assigned to</th><th>Use (guess)</th><th>Label</th></tr></thead>
        <tbody>
          {d.samples.map((k) => (
            <tr key={k.id} style={k.bucket === 'real' ? { background: 'rgba(239,68,68,0.08)' } : undefined}>
              <td><Link to={`/secrets/keys/${k.id}`}><code style={{ fontSize: 12 }}>{k.key_redacted}</code></Link></td>
              <td><RiskBadge risk={k.risk_level} type={k.secret_type} /></td>
              <td><KeyStatusBadge status={k.status} /></td>
              <td style={{ fontSize: 12 }}>{k.shape || '—'} · {k.key_name || '—'}</td>
              <td style={{ fontSize: 12, maxWidth: 320 }}>{k.key_use}</td>
              <td style={{ fontSize: 12, color: k.bucket === 'real' ? '#f87171' : 'var(--text-secondary)' }}>
                {k.bucket === 'real' ? 'labelled real' : k.bucket === 'fp' ? `false positive (${FP_REASON_LABEL[k.fp_reason] || k.fp_reason})` : 'open'}
              </td>
            </tr>
          ))}
          {!d.samples.length && <tr><td colSpan={6} style={{ color: 'var(--text-muted)' }}>No keys match this rule yet.</td></tr>}
        </tbody>
      </table>
      <button className="btn btn-ghost" style={{ marginTop: 6 }} onClick={onClose}>Close</button>
    </div>
  );
}

function AddRule({ filters, onAdded }) {
  const [f, setF] = useState({ name: '', reason: 'pattern', rule_type: 'shape', expression: '', gitleaks_rule: '*' });
  const [msg, setMsg] = useState('');
  const submit = (e) => {
    e.preventDefault();
    axios.post('/secrets/fp-rules', f).then(() => { setMsg('Added as a proposal'); setF({ ...f, name: '', expression: '' }); onAdded(); })
      .catch((err) => setMsg(errMsg(err)));
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="card" style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ width: 200 }}><label className="form-label">Name</label>
        <input className="form-input" value={f.name} onChange={set('name')} placeholder="Optional" /></div>
      <div style={{ width: 130 }}><label className="form-label">Matches on</label>
        <select className="form-input" value={f.rule_type} onChange={set('rule_type')}>
          <option value="shape">Value shape</option><option value="key_name">Assigned to</option><option value="path_glob">Path</option>
        </select></div>
      <div style={{ width: 220 }}><label className="form-label">{f.rule_type === 'shape' ? 'Shape' : f.rule_type === 'key_name' ? 'Name pattern' : 'Path glob'}</label>
        {f.rule_type === 'shape' ? (
          <select className="form-input" value={f.expression} onChange={set('expression')}>
            <option value="">Pick a shape</option>
            {(filters?.shapes || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : (
          <input className="form-input" value={f.expression} onChange={set('expression')}
            placeholder={f.rule_type === 'key_name' ? 'e.g. *client_id' : 'e.g. **/tests/**'} />
        )}
      </div>
      <div style={{ width: 200 }}><label className="form-label">Reason</label>
        <select className="form-input" value={f.reason} onChange={set('reason')}>
          {Object.entries(FP_REASON_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select></div>
      <div style={{ width: 180 }}><label className="form-label">Detection</label>
        <input className="form-input" value={f.gitleaks_rule} onChange={set('gitleaks_rule')} placeholder="* = any generic" /></div>
      <button className="btn btn-secondary" disabled={!f.expression}>Add rule</button>
      {msg && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{msg}</span>}
    </form>
  );
}

export default function SecretRules() {
  const filters = useSecretFilters();
  const { can_edit: canEdit } = useSecretsAccess();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [showClosed, setShowClosed] = useState(false);

  const load = useCallback(() => {
    axios.get('/secrets/fp-rules').then((r) => setD(r.data)).catch((e) => setMsg(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  const after = (m) => { setMsg(m); invalidateFilters(); load(); };

  const learn = () => {
    setBusy(true); setMsg('Learning…');
    axios.post('/secrets/fp-rules/learn').then((r) => after(
      `Done: ${r.data.proposed} new proposal(s), ${r.data.scored} rules re-scored on ${r.data.labelled_fp} false positives and ${r.data.labelled_real} real keys.`))
      .catch((e) => setMsg(errMsg(e))).finally(() => setBusy(false));
  };
  const setStatus = (r, status) => {
    let force = false;
    if (status === 'approved' && !r.meets_floor) {
      if (!window.confirm(`This rule is under the floor (${r.precision_pct ?? 'no'}% precision on ${r.support} examples). Approve anyway?`)) return;
      force = true;
    }
    if (status === 'rejected' && !window.confirm(`Reject "${r.name}"? The learner will not propose it again.`)) return;
    const note = status === 'suspended' || status === 'rejected' ? window.prompt('Why? (recorded in the audit log)') || '' : '';
    axios.patch(`/secrets/fp-rules/${r.id}`, { status, force, status_note: note })
      .then(() => after(`Rule "${r.name}" is now ${status}.`)).catch((e) => setMsg(errMsg(e)));
  };
  const apply = (r) => {
    axios.post(`/secrets/fp-rules/${r.id}/apply`, { dry_run: true }).then((res) => {
      const n = res.data.would_mark;
      if (!n) { setMsg('No open keys match this rule.'); return; }
      if (!window.confirm(`Mark ${n} open key(s) as automatic false positives under "${r.name}"? They stay reviewable.`)) return;
      axios.post(`/secrets/fp-rules/${r.id}/apply`, {}).then((r2) => after(`${r2.data.marked} key(s) marked.`)).catch((e) => setMsg(errMsg(e)));
    }).catch((e) => setMsg(errMsg(e)));
  };

  const rows = d?.data || [];
  const visible = rows.filter((r) => showClosed || !['rejected'].includes(r.status));
  const floors = { precision_min: d?.precision_min, support_min: d?.support_min };

  return (
    <div>
      <PageHeader title="Secrets rotation · Rules">
        Rules mark false positives automatically. The learner proposes them from the reasons people give; a proposal
        applies only after approval, needs {d?.precision_min ?? 95}% precision on {d?.support_min ?? 5} labelled keys, and is
        suspended when reviews reject its marks. Every automatic mark can be reviewed under Keys.
      </PageHeader>
      <div className="page-body">
        <SecretsTabs />
        <div className="toolbar">
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {d?.last_learned_at ? `Learner last ran ${fmtDateTime(d.last_learned_at)}.` : 'The learner has not run yet.'} It also runs weekly.
          </span>
          <span className="spacer" />
          <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} /> Show rejected
          </label>
          {canEdit && <button className="btn btn-primary" disabled={busy} onClick={learn}>Run learner now</button>}
        </div>
        {canEdit && <AddRule filters={filters} onAdded={load} />}
        {msg && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>{msg}</div>}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Rule</th><th>Matches on</th><th>Reason</th><th>Status</th><th>Precision</th>
                <th>Would mark</th><th>Marked · confirmed · rejected</th><th />
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <React.Fragment key={r.id}>
                  <tr>
                    <td>
                      <div>{r.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>#{r.id} · {r.source}{r.gitleaks_rule !== '*' ? ` · ${r.gitleaks_rule}` : ' · generic detections'}</div>
                    </td>
                    <td style={{ fontSize: 12 }}>{TYPE_LABEL[r.rule_type]} <code>{r.expression}</code></td>
                    <td style={{ fontSize: 12 }}>{FP_REASON_LABEL[r.reason] || r.reason}</td>
                    <td><RuleStatus status={r.status} />
                      {r.status_note && <div style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 200 }}>{r.status_note}</div>}</td>
                    <td style={{ fontSize: 12 }}><Precision r={r} floors={floors} /></td>
                    <td style={{ fontSize: 12 }}>{r.matches_open} open</td>
                    <td style={{ fontSize: 12 }}>{r.auto_marked} · {r.review_confirmed} · <span style={{ color: r.review_rejected ? '#f87171' : undefined }}>{r.review_rejected}</span></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-ghost" onClick={() => setOpen(open === r.id ? null : r.id)}>{open === r.id ? 'Hide' : 'Examples'}</button>
                      {canEdit && r.status === 'proposed' && <>
                        <button className="btn btn-secondary" onClick={() => setStatus(r, 'approved')}>Approve</button>{' '}
                        <button className="btn btn-ghost" onClick={() => setStatus(r, 'rejected')}>Reject</button></>}
                      {canEdit && r.status === 'approved' && <>
                        <button className="btn btn-secondary" onClick={() => apply(r)}>Apply to backlog</button>{' '}
                        <button className="btn btn-ghost" onClick={() => setStatus(r, 'suspended')}>Suspend</button></>}
                      {canEdit && (r.status === 'suspended' || r.status === 'rejected') && (
                        <button className="btn btn-ghost" onClick={() => setStatus(r, 'proposed')}>Re-open</button>)}
                    </td>
                  </tr>
                  {open === r.id && <tr><td colSpan={8} style={{ padding: 0 }}><RuleDetail id={r.id} onClose={() => setOpen(null)} /></td></tr>}
                </React.Fragment>
              ))}
              {d && !visible.length && <tr><td colSpan={8} style={{ color: 'var(--text-muted)', padding: 16 }}>No rules yet. Run the learner to seed proposals.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
